import { type Filter, type Input } from "@/workflow_api/types/workflow";
import type { NodeVariable } from "../../types";
import type { VariableFileName } from "../../doc-generator/context";
import type { Variable } from "../../types";

export const getSerializedVariable = (
  value: unknown,
): {
  value:
    | string
    | number
    | null
    | boolean
    | Array<Filter>
    | Array<string>
    | VariableFileName;
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME";
  connection?: string;
  field?: string;
  output_name?: string;
} => {
  if (value === null) {
    return {
      value: null,
    };
  }

  if (typeof value === "string") {
    return {
      value,
    };
  }
  const variable = value as Variable;
  console.log(variable);
  if (variable.global_type) {
    if (
      variable.global_type === "APPLICANT" ||
      variable.global_type === "CURRENT_TIME"
    ) {
      return {
        value: variable.id,
        global_type: variable.global_type,
      };
    }
    return {
      value: variable.node_id || null,
      global_type: variable.global_type,
    };
  }

  if (variable.output_name && variable.field_id) {
    return {
      value: null,
      connection: variable.node_id,
      field: variable.field_id,
      output_name: variable.output_name,
    };
  }

  if (variable.output_name) {
    return {
      value: null,
      connection: variable.node_id,
      output_name: variable.output_name,
    };
  }

  if (variable.field_id) {
    return {
      value: null,
      connection: variable.node_id,
      field: variable.field_id,
    };
  }

  /* if (variable.id === variable.node_id) {
    return {
      value: null,
      connection: (value as Variable).node_id,
    }; */
  return {
    value: null,
    connection: (value as Variable).node_id,
  };
  // }

  // // Cuando la variable es un campo específico del nodo (no el nodo completo)
  // const serializedInput: Input[number] = {
  //   value: null,
  //   connection: variable.node_id,
  //   field: variable.id, // Usar 'field' en lugar de 'field_id' (compatibilidad con backend)
  //   name: "description", // override name to match resolution input
  //   type: "STRING",
  // };

  // return serializedInput;
};

/**
 * Serialize the resolution inputs into the expected format.
 *
 * @param status
 * @param attachments
 * @param description
 * @param descriptionVariable
 * @returns
 */
export const serializeResolutionInputs = (
  status: string,
  attachments: string[],
  description: string | null,
  descriptionVariable: Variable | null,
  nodeVariables?: NodeVariable[],
): Input => {
  const descriptionInput: Input[number] = {
    name: "description",
    type: "STRING",
    // Si hay una variable seleccionada, value debe ser null para que la conexión funcione
    value: descriptionVariable ? null : (description ?? null),
  };

  // Agregar propiedades de conexión si hay una variable seleccionada
  if (descriptionVariable) {
    console.log("descriptionVariable", descriptionVariable);
    descriptionInput.connection = descriptionVariable.node_id;

    // Si es un global_type, agregarlo
    if (descriptionVariable.global_type) {
      if (
        descriptionVariable.global_type === "APPLICANT" ||
        descriptionVariable.global_type === "CURRENT_TIME"
      ) {
        descriptionInput.global_type = descriptionVariable.global_type;
        descriptionInput.value = descriptionVariable.id;
        delete descriptionInput.connection;
      } else {
        descriptionInput.global_type = descriptionVariable.global_type;
        descriptionInput.value = descriptionVariable.node_id || null;
        delete descriptionInput.connection;
      }
    } else if (
      descriptionVariable.field_id &&
      descriptionVariable.output_name
    ) {
      descriptionInput.field = descriptionVariable.field_id;
      descriptionInput.output_name = descriptionVariable.output_name;
    } else if (descriptionVariable.output_name) {
      descriptionInput.output_name = descriptionVariable.output_name;
    }
    // Si NO es el output completo del nodo (es un campo específico)
    else if (descriptionVariable.id !== descriptionVariable.node_id) {
      descriptionInput.field = descriptionVariable.id;
    }
  }

  return [
    {
      name: "status",
      type: "STRING",
      value: status,
    },
    descriptionInput,
  ].concat(
    attachments.map((fileId) => {
      let foundVariable: Variable | undefined;
      let foundNodeId: string | undefined;

      for (const nodeVar of nodeVariables || []) {
        const variable = nodeVar.variables.find((v) => v.id === fileId);
        if (variable) {
          foundVariable = variable;
          foundNodeId = nodeVar.nodeId;
          break;
        }
      }

      const isSignedDocument =
        foundVariable && foundVariable.id !== foundVariable.node_id;

      const result = {
        name: "files",
        type: "STRING",
        //value: foundVariable?.value ?? null,
        value: isSignedDocument
          ? (foundVariable?.name ?? null)
          : (foundVariable?.value ?? null),
        connection: foundNodeId || fileId,
      };

      return result;
    }),
  );
};

/**
 * Deserializes the status from the inputs array.
 * @param inputs
 * @returns
 */
export const getStatusFromInput = (inputs: Input): string => {
  try {
    const statusInput = inputs.find((input) => input.name === "status");
    // check values from ResolutionSelector.tsx
    if (
      statusInput &&
      ["approved", "denied", "canceled", "unfair"].includes(
        statusInput.value as string,
      )
    ) {
      return statusInput.value as string;
    }
  } catch (error) {
    console.error("Error getting status from inputs:", error);
  }
  return "";
};

/**
 * TODO: - search for variable with outputs
 * @param descriptionInput
 * @param variables
 * @returns
 */
const searchVariable = (
  descriptionInput: Input[0],
  variables: NodeVariable[],
) => {
  if (descriptionInput && descriptionInput.source_connections?.[0]) {
    const connection = descriptionInput.source_connections[0];
    const node = variables.find((v) => v.nodeId === connection.source_node_id);

    // Prioridad: field del input > field_id del connection > field del connection
    const fieldId =
      descriptionInput.field || connection.field_id || connection.field;
    const output_name = connection.output_name;

    if (fieldId && output_name) {
      if (node) {
        const foundVar = node.variables.find(
          (varItem) => varItem.id === `${fieldId}-${output_name}`,
        );
        return foundVar;
      } else {
        // Si no encuentra el nodo, buscar en todas las variables
        const allVariables = variables.flatMap((v) => v.variables);
        const foundVar = allVariables.find(
          (varItem) =>
            varItem.id === `${fieldId}-${output_name}` &&
            varItem.node_id === connection.source_node_id,
        );
        return foundVar;
      }
    }

    // Buscar por field_id si existe
    if (fieldId) {
      if (node) {
        const foundVar = node.variables.find(
          (varItem) => varItem.id === fieldId,
        );
        return foundVar;
      } else {
        // Si no encuentra el nodo, buscar en todas las variables
        const allVariables = variables.flatMap((v) => v.variables);
        const foundVar = allVariables.find(
          (varItem) =>
            varItem.id === fieldId &&
            varItem.node_id === connection.source_node_id,
        );
        return foundVar;
      }
    }
    if (output_name) {
      if (node) {
        const foundVar = node.variables.find(
          (varItem) =>
            varItem.id === `${connection.source_node_id}-${output_name}`,
        );
        return foundVar;
      } else {
        // Si no encuentra el nodo, buscar en todas las variables
        const allVariables = variables.flatMap((v) => v.variables);
        const foundVar = allVariables.find(
          (varItem) =>
            varItem.id === `${connection.source_node_id}-${output_name}` &&
            varItem.node_id === connection.source_node_id,
        );
        return foundVar;
      }
    }
    // Si no hay field ni field_id, es el output completo del nodo
    else {
      const allVariables = variables.flatMap((v) => v.variables);
      const foundVar = allVariables.find(
        (varItem) =>
          varItem.node_id === connection.source_node_id &&
          varItem.node_id === varItem.id,
      );
      return foundVar;
    }
  }
};

export const getCommentFromInput = (
  inputs: Input,
  variables: NodeVariable[],
): {
  variable: Variable | null;
  value: string;
  isVariable: boolean;
} => {
  try {
    const descriptionInput = inputs.find(
      (input) => input.name === "description",
    );
    if (descriptionInput) {
      if (descriptionInput.global_type) {
        return {
          variable: {
            id: descriptionInput.value as string,
            node_id: "global-variables",
            global_type: descriptionInput.global_type,
            name: "",
            type: "string",
          },
          value: "",
          isVariable: true,
        };
      }
      if (descriptionInput.value) {
        return {
          variable: null,
          value: descriptionInput.value as string,
          isVariable: false,
        };
      } else if (descriptionInput.source_connections?.[0]) {
        const variable = searchVariable(descriptionInput, variables);
        console.log(variable);
        if (variable) {
          return {
            variable,
            value: "",
            isVariable: true,
          };
        }
      }
    }
  } catch (error) {
    console.error("Error getting comment from inputs:", error);
  }
  return { variable: null, value: "", isVariable: false };
};

export const getAttachmentsFromInput = (
  inputs: Input,
  variables?: NodeVariable[],
): string[] => {
  try {
    const filesInput = inputs.filter((input) => input.name === "files");
    return filesInput
      .map((input) => {
        if (input.value && input.source_connections?.[0]?.source_node_id) {
          const sourceNodeId = input.source_connections[0].source_node_id;

          if (variables) {
            for (const nodeVar of variables) {
              const foundVar = nodeVar.variables.find(
                (v) => v.name === input.value && v.node_id === sourceNodeId,
              );
              if (foundVar) {
                return foundVar.id;
              }
            }
          }
        }

        if (input.source_connections?.[0]?.source_node_id) {
          return input.source_connections[0].source_node_id;
        }

        return input.connection;
      })
      .filter((v): v is string => typeof v === "string" && v !== "");
  } catch (error) {
    console.error("Error proveniente de inputs:", error);
  }
  return [];
};
